import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import ThemePreferencesSync from "./components/ThemePreferencesSync";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import AgentDashboard from "./pages/AgentDashboard";
import CaseDetail from "./pages/CaseDetail";
import ActivityDayDetail from "./pages/ActivityDayDetail";
import ClientsManagement from "./pages/ClientsManagement";
import SecurityEvents from "./pages/SecurityEvents";
import Install from "./pages/Install";
import InstallBanner from "./components/InstallBanner";

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requireAdmin && user.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

// Auth Route (redirect if already logged in)
function AuthRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/install" element={<Install />} />
            
            <Route 
                path="/login" 
                element={
                    <AuthRoute>
                        <Login />
                    </AuthRoute>
                } 
            />

            {/* Admin Routes */}
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Clients Management (Admin only) */}
            <Route 
                path="/clients" 
                element={
                    <ProtectedRoute requireAdmin>
                        <ClientsManagement />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/admin/security"
                element={
                    <ProtectedRoute requireAdmin>
                        <SecurityEvents />
                    </ProtectedRoute>
                }
            />

            {/* Agent Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <AgentDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Case Routes */}
            <Route 
                path="/cases/:caseId" 
                element={
                    <ProtectedRoute>
                        <CaseDetail />
                    </ProtectedRoute>
                } 
            />

            {/* Activity Day Routes */}
            <Route 
                path="/cases/:caseId/days/:dayId" 
                element={
                    <ProtectedRoute>
                        <ActivityDayDetail />
                    </ProtectedRoute>
                } 
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <ThemePreferencesSync />
                        <InstallBanner />
                        <AppRoutes />
                        <Toaster />
                    </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
